import { hasValidatedDeterministicScreening } from './deterministicScreeningValidation';
import { containsTruthViolation } from '../audit/qaSeverityPolicy';
import type { SiteRagReport } from '../../types/siteRag';
import {
  isRetiredWithheldNarrativeDeliveryMessage,
  isWithheldSectionOmissionDetail,
  withheldNarrativeLeaves,
} from '../report/withheldNarrative';
import {
  artifactManifestV1Schema,
  deliveryDecisionSchema,
  presentationSnapshotV1Schema,
  projectionManifestV1Schema,
  reportDocumentModelV1Schema,
  reportRevisionReferenceV1Schema,
  type ArtifactManifestV1,
  type DeliveryDecision,
  type PresentationSnapshotV1,
  type ReportDocumentModelV1,
} from '../../types/reportContractsV2';

export type ReportBodyState = 'ready' | 'body-unavailable' | 'metadata-only';
export type DeliveryAuthorizationState = 'authorized' | 'unauthorized' | 'expired';
export type LegacyArtifactPolicy = 'require-v2-artifact' | 'allow-validated-full-body';

export interface EvaluateReportDeliveryInput {
  report: Pick<
    SiteRagReport,
    | 'id'
    | 'summary'
    | '_metadataOnly'
    | 'generationTier'
    | 'isDraft'
    | 'qualityHold'
    | 'qualityHoldFindings'
    | 'qualityReview'
    | 'degradedMode'
    | 'degradedReason'
    | 'qaFindings'
    | 'reportRevision'
  > & Partial<SiteRagReport>;
  bodyState?: ReportBodyState;
  authorization?: DeliveryAuthorizationState;
  document?: ReportDocumentModelV1 | unknown;
  presentation?: PresentationSnapshotV1 | unknown;
  artifactManifest?: ArtifactManifestV1 | unknown;
  projectionManifests?: readonly unknown[];
  retiredEvidence?: boolean;
  /**
   * Compatibility escape hatch for a fully loaded legacy report that is
   * synchronously converted and validated by the existing exporter. New V2
   * API/publish boundaries must use the default `require-v2-artifact` policy.
   */
  legacyArtifactPolicy?: LegacyArtifactPolicy;
}

type DeliveryReason = DeliveryDecision['reasons'][number];

function inferredBodyState(report: EvaluateReportDeliveryInput['report']): ReportBodyState {
  if (report._metadataOnly) return 'metadata-only';
  return typeof report.summary === 'string' ? 'ready' : 'body-unavailable';
}

function pushReason(
  reasons: DeliveryReason[],
  reason: DeliveryReason,
): void {
  if (!reasons.some((entry) => entry.code === reason.code && entry.message === reason.message)) {
    reasons.push(reason);
  }
}

/** See the replay filter: the exact strings the truth-violation path stamped. */
export const HISTORICAL_TRUTH_STAMP_MESSAGES = new Set([
  'The report states something the evidence contradicts.',
  'The document revision records a failed hard quality gate.',
  'The document revision records a failed quality outcome.',
  'The artifact manifest records a failed quality outcome.',
]);

/**
 * AUDIT-ADVISORY (2026-08-22) — the ONE predicate for delivery reasons that
 * are audit outcomes rather than artifact defects: the 'quality-held' stamp,
 * the four messages the truth-violation path exclusively minted, and the
 * retired withheld-narrative stamp (2026-09-18 owner decision; see
 * `isRetiredWithheldNarrativeDeliveryMessage`). Every consumer of a PERSISTED
 * deliveryDecision (client artifact requests, server enqueue validation, the
 * artifact viewer) must filter through this instead of reading the immutable
 * stamp raw — stamps cannot be rewritten, so the read side is where history
 * gets the policy.
 */
export function isAuditAdvisoryDeliveryReason(
  reason: Pick<DeliveryReason, 'code' | 'message'>,
): boolean {
  return reason.code === 'quality-held'
    || (reason.code === 'hard-gate-failed' && (
      HISTORICAL_TRUTH_STAMP_MESSAGES.has(reason.message)
      || isRetiredWithheldNarrativeDeliveryMessage(reason.message)
    ));
}

/**
 * Re-derive a persisted decision with audit-advisory reasons removed —
 * exactly the decision evaluateReportDelivery would return today. Returns the
 * input unchanged when nothing filters (no churn for clean decisions).
 */
export function advisoryFilteredDeliveryDecision(decision: DeliveryDecision): DeliveryDecision {
  const reasons = decision.reasons.filter((reason) => !isAuditAdvisoryDeliveryReason(reason));
  if (reasons.length === decision.reasons.length) return decision;
  const canViewPrivate = decision.canViewPrivate;
  const canDeliver = canViewPrivate && reasons.length === 0;
  return deliveryDecisionSchema.parse({
    schemaVersion: 'delivery-decision-v1',
    state: canDeliver ? 'ready' : canViewPrivate ? 'view-only' : 'blocked',
    canViewPrivate,
    canExport: canDeliver,
    canPublish: canDeliver,
    reasons,
  });
}

/** Artifact creation uses the complete readable source, independently of PDF delivery. */
export function deliveryDecisionAllowsReportContent(decision: DeliveryDecision): boolean {
  return decision.canViewPrivate && decision.reasons.every(reason => (
    reason.code === 'artifact-unavailable'
    || reason.code === 'layout-pending'
    || reason.code === 'layout-invalid'
    || reason.code === 'projection-incomplete'
  ));
}

/** A printable artifact can change state without changing the saved content.
 * Audit policy is applied by each consumer; SED claim ingestion stays strict. */
export function hasIndependentReportContent(delivery: unknown, decision: DeliveryDecision): boolean {
  if (!delivery || typeof delivery !== 'object' || !('status' in delivery)) return false;
  if (!['not-requested', 'ready', 'failed'].includes(String(delivery.status))) return false;
  return deliveryDecisionAllowsReportContent(advisoryFilteredDeliveryDecision(decision));
}

export function canBuildReportArtifacts(delivery: unknown, decision: DeliveryDecision): boolean {
  if (!delivery || typeof delivery !== 'object' || !('status' in delivery)) return false;
  const lazy = delivery.status === 'not-requested';
  const failed = delivery.status === 'failed'
    && 'retryable' in delivery && delivery.retryable === true
    && 'errorCode' in delivery && delivery.errorCode === 'PDF_DELIVERY_FAILED';
  if (!lazy && !failed) return false;
  return deliveryDecisionAllowsReportContent(advisoryFilteredDeliveryDecision(decision));
}

/** The administrative recovery route remains restricted to a known failed PDF. */
export function canRetryReportPdfDelivery(delivery: unknown, decision: DeliveryDecision): boolean {
  return Boolean(delivery && typeof delivery === 'object'
    && 'status' in delivery && delivery.status === 'failed'
    && canBuildReportArtifacts(delivery, decision));
}

function hardReason(code: DeliveryReason['code'], message: string): DeliveryReason {
  return { code, message, overrideable: false };
}

/**
 * One deterministic, fail-closed delivery decision for private view, every
 * export format and publishing. It performs no I/O and never infers success
 * from a job status alone.
 */
export function evaluateReportDelivery(input: EvaluateReportDeliveryInput): DeliveryDecision {
  const reasons: DeliveryReason[] = [];
  const authorization = input.authorization ?? 'authorized';
  const bodyState = input.bodyState ?? inferredBodyState(input.report);

  if (authorization === 'unauthorized') {
    pushReason(reasons, hardReason('unauthorized', 'The requester is not authorized to load this report revision.'));
  } else if (authorization === 'expired') {
    pushReason(reasons, hardReason('expired', 'The report artifact access grant has expired.'));
  }

  if (bodyState === 'metadata-only') {
    pushReason(
      reasons,
      hardReason(
        'metadata-only',
        'Only report metadata is available; it cannot substitute for the immutable report body.',
      ),
    );
  } else if (bodyState === 'body-unavailable') {
    pushReason(reasons, hardReason('body-unavailable', 'The immutable report body could not be loaded.'));
  }

  /*
   * AUDIT-ADVISORY POLICY (2026-08-22, William) — quality holds no longer bar
   * delivery AT ALL. The 2026-08-05 policy made the reviewer verdict advisory
   * but kept truth-violation holds export-barring; the product decision now is
   * that the user always reads AND exports the report, and the hold becomes an
   * "audit recommended" notice on the report page. `qualityHold` and the truth
   * questions remain STAMPED (they drive that notice, the verdict's audit-
   * pending caveat, and the composite's 0.5 confidence discount) — they simply
   * stop producing delivery reasons. The PUBLIC publish gate is unaffected:
   * sharing outward still runs the full audit gate (publishGate.ts).
   */
  if (
    !input.report.qualityReview
    && !hasValidatedDeterministicScreening(input.report)
    && input.legacyArtifactPolicy !== 'allow-validated-full-body'
  ) {
    pushReason(
      reasons,
      hardReason('degraded', 'The final narrative reviewer did not produce a valid verdict.'),
    );
  }

  // Tier is an independent hard decision. In particular, a pre-review legacy
  // draft must not become exportable merely because the compatibility path does
  // not manufacture a reviewer verdict that never existed on historical data.
  // isDraft is OVERLOADED: genuine legacy drafts set it, but the quality-hold
  // era ALSO stamped it as the export-bar mirror on held investment-grade
  // reports. With a hold stamp present and no explicit draft tier, isDraft is
  // the hold's shadow — audit-advisory, not a draft (2026-08-22).
  if (
    input.report.generationTier === 'draft'
    || (input.report.isDraft === true && input.report.qualityHold !== 'needs-revision')
  ) {
    pushReason(reasons, hardReason('draft', 'Draft-tier reports are private review artifacts and cannot be delivered.'));
  }

  if (input.report.degradedMode) {
    pushReason(
      reasons,
      hardReason(
        'degraded',
        input.report.degradedReason?.trim()
          ? `The report uses degraded content: ${input.report.degradedReason}`
          : 'The report uses degraded content and cannot be exported or published.',
      ),
    );
  }

  // Truth violations no longer bar delivery either (audit-advisory policy —
  // they are the SAME summarizeQaSeverity predicate that mints qualityHold, so
  // leaving this bar in place would have made the hold removal a silent no-op).
  // They surface as answerable truth questions: the recommended audit.

  /*
   * WITHHELD NARRATIVE — AUDIT-ADVISORY (owner decision 2026-09-18, William:
   * "Allow export"). A leaf the reviewer-voice gate withheld (recorded on the
   * `reviewer-voice-leak` quality warning) HOLDS the report — `qualityHold`
   * 'needs-revision' via resolveQualityHold, a 'held' decision analysis, and
   * the "Not assessed in this report … treat as open, not cleared." notice
   * at that leaf on every surface, exports included — but, like every other
   * quality hold under the 2026-08-22 policy above, it produces NO delivery
   * reason: the user reads, exports and publishes the held report with the
   * notice in place. (The 734f35d52 candidate stamped a non-advisory
   * 'hard-gate-failed' reason here; a revision carrying that stamp replays it
   * as advisory — see isAuditAdvisoryDeliveryReason.) Positive-decision
   * eligibility is NOT decided here: Path to GO reinstates its own hold for a
   * withheld leaf (reportPlan.ts) until the owner rules on GO counting.
   */

  if (input.retiredEvidence) {
    pushReason(reasons, hardReason('retired-evidence', 'The report depends on retired evidence and must be regenerated.'));
  }

  let document: ReportDocumentModelV1 | undefined;
  if (input.document !== undefined) {
    const result = reportDocumentModelV1Schema.safeParse(input.document);
    if (!result.success) {
      pushReason(reasons, hardReason('schema-invalid', 'The report document revision failed runtime validation.'));
    } else {
      document = result.data;
      // 'held' is audit-advisory (see policy above). A FROZEN document's
      // hardGatesPassed=false / state 'failed' from the pre-advisory era was
      // minted from containsTruthViolation (the body was present at finalize,
      // so metadata-only is impossible there) — when the report's own
      // qaFindings still carry those violations, the stamped failure is the
      // truth audit, not an artifact defect, and reads as advisory.
      const stampedFailureIsTruthAudit = containsTruthViolation(input.report.qaFindings);
      if (!document.qualityDecision.hardGatesPassed && !stampedFailureIsTruthAudit) {
        pushReason(reasons, hardReason('hard-gate-failed', 'The document revision records a failed hard quality gate.'));
      }
      if (document.qualityDecision.state === 'degraded') {
        pushReason(reasons, hardReason('degraded', 'The document revision is a degraded, view-only artifact.'));
      } else if (document.qualityDecision.state === 'draft') {
        pushReason(reasons, hardReason('draft', 'The document revision is a draft, view-only artifact.'));
      } else if (document.qualityDecision.state === 'failed' && !stampedFailureIsTruthAudit) {
        pushReason(reasons, hardReason('hard-gate-failed', 'The document revision records a failed quality outcome.'));
      }
      // A required section whose ONLY content was a withheld narrative leaf is
      // frozen as an explicit omission with the withheld detail (the section
      // is not assessed, honestly). That is the same withheld-narrative hold,
      // not a missing section, so it follows the audit-advisory decision
      // above and never bars delivery — but only while the report itself
      // carries the withheld record the detail was built from. Any other
      // required omission still bars delivery.
      const withheldRecorded = withheldNarrativeLeaves(input.report).length > 0;
      if (document.omissions.some((omission) => (
        omission.required
        && !(withheldRecorded && isWithheldSectionOmissionDetail(omission.detail))
      ))) {
        pushReason(reasons, hardReason('schema-invalid', 'One or more required document sections were explicitly omitted.'));
      }
    }
  }

  let presentation: PresentationSnapshotV1 | undefined;
  if (input.presentation !== undefined) {
    const result = presentationSnapshotV1Schema.safeParse(input.presentation);
    if (!result.success) {
      pushReason(reasons, hardReason('schema-invalid', 'The frozen presentation snapshot failed runtime validation.'));
    } else {
      presentation = result.data;
    }
  }

  let artifactManifest: ArtifactManifestV1 | undefined;
  if (input.artifactManifest !== undefined) {
    const result = artifactManifestV1Schema.safeParse(input.artifactManifest);
    if (!result.success) {
      pushReason(reasons, hardReason('schema-invalid', 'The artifact manifest failed runtime validation.'));
    } else {
      artifactManifest = result.data;
      if (artifactManifest.qualityState === 'degraded') {
        pushReason(reasons, hardReason('degraded', 'The artifact manifest records a degraded quality outcome.'));
      } else if (artifactManifest.qualityState === 'draft') {
        pushReason(reasons, hardReason('draft', 'The artifact manifest records a draft quality outcome.'));
      } else if (
        artifactManifest.qualityState === 'failed'
        && !containsTruthViolation(input.report.qaFindings)
      ) {
        pushReason(reasons, hardReason('hard-gate-failed', 'The artifact manifest records a failed quality outcome.'));
      }
    }
  }

  const revisionResult = input.report.reportRevision
    ? reportRevisionReferenceV1Schema.safeParse(input.report.reportRevision)
    : null;
  if (revisionResult && !revisionResult.success) {
    pushReason(reasons, hardReason('schema-invalid', 'The report revision pointer failed runtime validation.'));
  }

  const revision = revisionResult?.success ? revisionResult.data : undefined;
  if (revision && document && (
    revision.revisionId !== document.revisionId
    || revision.documentHash !== document.contentHash
  )) {
    pushReason(reasons, hardReason('schema-invalid', 'The revision pointer does not match the loaded document identity.'));
  }
  if (revision && presentation && revision.presentationHash !== presentation.presentationHash) {
    pushReason(reasons, hardReason('schema-invalid', 'The revision pointer does not match the frozen presentation identity.'));
  }
  if (artifactManifest && document && artifactManifest.sourceDocumentHash !== document.contentHash) {
    pushReason(reasons, hardReason('schema-invalid', 'The artifact manifest was built from a different document hash.'));
  }
  if (artifactManifest && revision && artifactManifest.reportRevisionId !== revision.revisionId) {
    pushReason(reasons, hardReason('schema-invalid', 'The artifact manifest belongs to a different report revision.'));
  }
  const pdf = artifactManifest?.artifacts.pdf ?? revision?.artifacts.pdf;
  if (revision) {
    for (const persistedReason of revision.deliveryDecision.reasons) {
      // Historical revisions carry audit-class stamps from the pre-advisory
      // policies. 'quality-held' is unambiguous by code. The four
      // 'hard-gate-failed' messages below were minted EXCLUSIVELY by the
      // truth-violation path: at finalize time the body was always present,
      // so a stamped hardGatesPassed=false / state='failed' could only come
      // from containsTruthViolation — never from metadata-only, and artifact
      // failures mint their own codes (schema-invalid, layout-invalid,
      // artifact-unavailable), which all still replay fail-closed. Filtering
      // these makes EXISTING held revisions exportable without regeneration —
      // revisions are immutable, their stamps cannot be rewritten.
      // The retired withheld-narrative stamp replays the same way (it was a
      // hold, and holds are audit-advisory). One predicate, so this replay and
      // advisoryFilteredDeliveryDecision cannot drift apart.
      if (isAuditAdvisoryDeliveryReason(persistedReason)) continue;
      pushReason(reasons, persistedReason);
    }
    if (!pdf || pdf.state !== 'ready') {
      pushReason(reasons, hardReason('artifact-unavailable', 'A verified immutable PDF artifact is not ready.'));
    } else if (pdf.layoutStatus === 'pending') {
      pushReason(reasons, hardReason('layout-pending', 'PDF layout validation has not completed.'));
    } else if (pdf.layoutStatus !== 'valid') {
      pushReason(reasons, hardReason('layout-invalid', 'The PDF artifact failed deterministic layout validation.'));
    }
  } else if (input.legacyArtifactPolicy !== 'allow-validated-full-body') {
    pushReason(
      reasons,
      hardReason(
        'legacy-validation-required',
        'This legacy report has not completed deterministic V2 conversion and artifact validation.',
      ),
    );
  }

  for (const candidate of input.projectionManifests ?? []) {
    const parsedProjection = projectionManifestV1Schema.safeParse(candidate);
    if (!parsedProjection.success) {
      pushReason(reasons, hardReason('schema-invalid', 'A surface projection manifest failed runtime validation.'));
      continue;
    }
    const projection = parsedProjection.data;
    if (!projection.complete || projection.entries.some((entry) => (
      entry.outcome === 'omitted' && !entry.policyReason?.trim()
    ))) {
      pushReason(
        reasons,
        hardReason(
          'projection-incomplete',
          `The ${projection.surface} projection does not account for every source block.`,
        ),
      );
    }
  }

  const accessBlocked = reasons.some((reason) => (
    reason.code === 'unauthorized'
    || reason.code === 'expired'
    || reason.code === 'body-unavailable'
    || reason.code === 'metadata-only'
  ));
  const canViewPrivate = !accessBlocked;
  const canDeliver = canViewPrivate && reasons.length === 0;
  const state: DeliveryDecision['state'] = canDeliver
    ? 'ready'
    : canViewPrivate
      ? 'view-only'
      : 'blocked';

  return deliveryDecisionSchema.parse({
    schemaVersion: 'delivery-decision-v1',
    state,
    canViewPrivate,
    canExport: canDeliver,
    canPublish: canDeliver,
    reasons,
  });
}

export class ReportDeliveryBlockedError extends Error {
  readonly name = 'ReportDeliveryBlockedError';
  constructor(
    readonly action: 'export' | 'publish',
    readonly decision: DeliveryDecision,
  ) {
    super(
      `Report ${action} blocked: ${decision.reasons.map((reason) => reason.message).join(' ')}`,
    );
  }
}

export function assertReportDeliveryAllowed(
  decision: DeliveryDecision,
  action: 'export' | 'publish',
): void {
  const allowed = action === 'export' ? decision.canExport : decision.canPublish;
  if (!allowed) throw new ReportDeliveryBlockedError(action, decision);
}
